<template>
  <div>
    <div class="container mt-4 card-container" id="cardGrid">
      <div class="row">
        <h4 v-if="user.loggedIn">All cards</h4>
        <hr v-if="user.loggedIn" class="hr-black-bold">

        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="filter-header-label">
            ↑ Filter
          </div>
          <div class="sort-filter-label">
            <div class="dropdown">
              <a href="" id="filterCardsDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                ↓ Sort
              </a>
              <ul class="dropdown-menu" aria-labelledby="filterCardsDropdown">
                <li><a class="dropdown-item" href="#" v-on:click="applyFilterOrderBy('title', 'ASC')"
                       :class="{'active' : (this.orderBy === 'title' && this.orderByDirection === 'ASC')}">A-Z</a></li>
                <li><a class="dropdown-item" href="#" v-on:click="applyFilterOrderBy('title', 'DESC')"
                       :class="{'active' : (this.orderBy === 'title' && this.orderByDirection === 'DESC')}">Z-A</a></li>
                <li><a class="dropdown-item" href="#" v-on:click="applyFilterOrderBy('lastEdited', 'DESC')"
                       :class="{'active' : (this.orderBy === 'lastEdited' && this.orderByDirection === 'DESC')}">Latest
                  up</a></li>
                <li><a class="dropdown-item" href="#" v-on:click="applyFilterOrderBy('lastEdited', 'ASC')"
                       :class="{'active' : (this.orderBy === 'lastEdited' && this.orderByDirection === 'ASC')}">Latest
                  down</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div class="filter-main-container p-4 mb-4">
          <!-- Categories Filter Row -->
          <div class="scroll-row-wrapper" :class="{'scroll-row-wrapper--expanded': categoryExpanded}">
            <div class="filter-pill scroll-row-all"
                 :class="{'active': (filter === 'all')}"
                 v-on:click="changeFilter('all')">
              All Categories
            </div>
            <button v-if="!categoryExpanded" type="button"
                    class="scroll-row-arrow scroll-row-arrow--left"
                    :class="{'is-disabled': !categoryCanScrollLeft, 'is-hidden': !categoryHasOverflow}"
                    aria-label="Scroll categories left"
                    v-on:click="scrollRow('categoryScroller', -1)">
              ‹
            </button>
            <div class="filter-row"
                 :class="categoryExpanded ? 'filter-row--wrap' : 'filter-row--scroll'"
                 ref="categoryScroller"
                 v-on:wheel.prevent="onRowWheel('categoryScroller', $event)"
                 v-on:scroll="updateCategoryScrollState">
              <div v-for="(item, index) in cardCategories" :key="'cat-'+index"
                   class="filter-pill" :class="{'active': (filter === item.idCategory)}"
                   v-on:click="changeFilter(item.idCategory)">
                {{ item.categoryName }}
              </div>
            </div>
            <button v-if="!categoryExpanded" type="button"
                    class="scroll-row-arrow scroll-row-arrow--right"
                    :class="{'is-disabled': !categoryCanScrollRight, 'is-hidden': !categoryHasOverflow}"
                    aria-label="Scroll categories right"
                    v-on:click="scrollRow('categoryScroller', 1)">
              ›
            </button>
            <button type="button"
                    class="scroll-row-arrow scroll-row-toggle"
                    :class="{'is-hidden': !categoryHasOverflow && !categoryExpanded}"
                    :aria-label="categoryExpanded ? 'Collapse categories' : 'Show all categories'"
                    :title="categoryExpanded ? 'Collapse' : 'Show all'"
                    v-on:click="toggleCategoryExpand">
              {{ categoryExpanded ? '«' : '»' }}
            </button>
          </div>

          <!-- Authors Filter Row -->
          <div class="scroll-row-wrapper" :class="{'scroll-row-wrapper--expanded': authorExpanded}">
            <div class="filter-pill scroll-row-all"
                 :class="{'active': (authorFilter === 'all')}"
                 v-on:click="changeAuthorFilter('all')">
              All Authors
            </div>
            <div class="filter-pill scroll-row-all"
                 v-if="user.loggedIn"
                 :class="{'active': (authorFilter === 'mine')}"
                 v-on:click="changeAuthorFilter('mine')">
              My Cards
            </div>
            <button v-if="!authorExpanded" type="button"
                    class="scroll-row-arrow scroll-row-arrow--left"
                    :class="{'is-disabled': !authorCanScrollLeft, 'is-hidden': !authorHasOverflow}"
                    aria-label="Scroll authors left"
                    v-on:click="scrollRow('authorScroller', -1)">
              ‹
            </button>
            <div class="filter-row"
                 :class="authorExpanded ? 'filter-row--wrap' : 'filter-row--scroll'"
                 ref="authorScroller"
                 v-on:wheel.prevent="onRowWheel('authorScroller', $event)"
                 v-on:scroll="updateAuthorScrollState">
              <div v-for="(author, index) in authors" :key="'auth-'+index"
                   class="filter-pill" :class="{'active': (authorFilter === author.idCompany)}"
                   v-on:click="changeAuthorFilter(author.idCompany)">
                {{ author.companyName }}
              </div>
            </div>
            <button v-if="!authorExpanded" type="button"
                    class="scroll-row-arrow scroll-row-arrow--right"
                    :class="{'is-disabled': !authorCanScrollRight, 'is-hidden': !authorHasOverflow}"
                    aria-label="Scroll authors right"
                    v-on:click="scrollRow('authorScroller', 1)">
              ›
            </button>
            <button type="button"
                    class="scroll-row-arrow scroll-row-toggle"
                    :class="{'is-hidden': !authorHasOverflow && !authorExpanded}"
                    :aria-label="authorExpanded ? 'Collapse authors' : 'Show all authors'"
                    :title="authorExpanded ? 'Collapse' : 'Show all'"
                    v-on:click="toggleAuthorExpand">
              {{ authorExpanded ? '«' : '»' }}
            </button>
          </div>

          <!-- Top Tags Filter Row -->
          <div class="scroll-row-wrapper" :class="{'scroll-row-wrapper--expanded': tagExpanded}">
            <div class="filter-pill scroll-row-all"
                 :class="{'active': (tagFilter === 'all')}"
                 v-on:click="changeTagFilter('all')">
              All Top Tags
            </div>
            <button v-if="!tagExpanded" type="button"
                    class="scroll-row-arrow scroll-row-arrow--left"
                    :class="{'is-disabled': !tagCanScrollLeft, 'is-hidden': !tagHasOverflow}"
                    aria-label="Scroll tags left"
                    v-on:click="scrollRow('tagScroller', -1)">
              ‹
            </button>
            <div class="filter-row"
                 :class="tagExpanded ? 'filter-row--wrap' : 'filter-row--scroll'"
                 ref="tagScroller"
                 v-on:wheel.prevent="onRowWheel('tagScroller', $event)"
                 v-on:scroll="updateTagScrollState">
              <div v-for="(tag, index) in topTags" :key="'tag-'+index"
                   class="filter-pill" :class="{'active': (tagFilter === tag.tag)}"
                   v-on:click="changeTagFilter(tag.tag)">
                {{ tag.tag }}
              </div>
            </div>
            <button v-if="!tagExpanded" type="button"
                    class="scroll-row-arrow scroll-row-arrow--right"
                    :class="{'is-disabled': !tagCanScrollRight, 'is-hidden': !tagHasOverflow}"
                    aria-label="Scroll tags right"
                    v-on:click="scrollRow('tagScroller', 1)">
              ›
            </button>
            <button type="button"
                    class="scroll-row-arrow scroll-row-toggle"
                    :class="{'is-hidden': !tagHasOverflow && !tagExpanded}"
                    :aria-label="tagExpanded ? 'Collapse tags' : 'Show all tags'"
                    :title="tagExpanded ? 'Collapse' : 'Show all'"
                    v-on:click="toggleTagExpand">
              {{ tagExpanded ? '«' : '»' }}
            </button>
          </div>
        </div>

        <div class="filter-header-label mb-3 mt-4">
          ⇄ Options
        </div>

        <div class="control-main-container p-4 mb-4">
          <div class="control-row">
            <span class="control-row-label">View</span>
            <a href="/#/card/map" class="control-pill control-pill--link">
              Show card map ↗
            </a>
          </div>
        </div>
      </div>


      <div class="row">
        <div class="col-auto mb-3">
          <a href="#"
             v-on:click="addCard()">
            <div class="card">
              <div class="card-body add-card">
                +&nbsp;<u>Add New Card</u>
              </div>
            </div>
          </a>
        </div>

        <div class="col-auto mb-3" v-for="card in cards" :key="card.idCard">
          <a href="#" data-bs-toggle="modal" :data-bs-target="'#cardModal' + uid"
             v-on:click="loadCard(card, false, true)">
            <div class="card" :style="{'background': card.cardColor}"
                 :class="{'lighten-card': !card.published, 'cardBorder': card.border}">
              <div class="card-body">
                <div class="row">
                  <div class="col-md-12 margin-bottom">
                    <div class="card-text text-uppercase special2" :style="{color: card.textColor}">
                      {{ card.title }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="card-footer">
                <div class="row">
                  <div class="col-md-12">
                    <div class="card-text special">
                      <div class="keep-bottom" :style="{color: card.textColor}">
                        {{ card.cardCompany.companyName }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>

      </div>
    </div>
    <!--End card-grid-->

    <AlertComponent type="nobb-alert-primary" message="Thank you, that you are into creating fancy cards.
        To do so, please register or login, so that we can safe your cards and you will never loose them!"
                    v-if="loginAlert"></AlertComponent>

    <!-- Start card modal render !-->
    <div class="modal fade" :id="'cardModal' + uid" tabindex="-1" role="dialog" aria-hidden="true" ref="cardModal">
      <div class="modal-dialog modal-lg" role="document">
        <div class="w-12 float-right"
             style="margin-left: auto; padding-right: 20px; padding-top: 10px; padding-bottom: 5px;">
          <div class="close-modal" style="color: white;">
            Close X
          </div>
        </div>

        <div class="w-12 d-flex align-items-center justify-content-center cardPinStripe"
             :style="'background: ' + cardEdit.cardColor">
        </div>

        <div class="modal-content" v-show="(cardEdit.new && !user.loggedIn)">
          <div class="modal-header">
            <div class="view-card-logo">
              <div class="logo-container">
                <h2>Oh no!</h2>
              </div>
            </div>

            <div class="modal-dismiss-container">
              <button type="button" class="close-override" data-bs-dismiss="modal" aria-label="close">
                <span aria-hidden="true" class="modal-dismiss">X</span>
              </button>
              <a class="small-subtext" style="color: black !important;" data-bs-dismiss="modal">close</a>
            </div>
          </div>
        </div>
        <div class="modal-content" :style="'background: white;'"
             v-show="!(cardEdit.new && !user.loggedIn)"
             style="background-blend-mode: screen;">
          <div class="pre-header" v-if="cardEdit.editMode" :style="{color: cardEdit.textColor}">
            <b :style="{color: cardEdit.textColor}"> Category</b> &nbsp; Which category does your card best fit into?
          </div>
          <div class="modal-header">
            <div class="category-selector d-flex justify-content-between">
              <div style="margin-left: 10px;"></div>

              <template v-for="(item, index) in cardCategories">
                <div class="category-item" :key="index" v-on:click="updateCategory(item.idCategory)"
                     :class="{'active': (cardEdit.cardCategory === item.idCategory)}"
                     :style="[cardEdit.cardCategory === item.idCategory ? {borderColor: cardEdit.textColor, color: cardEdit.textColor} : {}]"
                     v-show="(cardEdit.cardCategory === item.idCategory) || cardEdit.editMode">{{ item.categoryName }}
                </div>
              </template>
            </div>


            <div class="modal-undo-container d-flex justify-content-between card-menubar">
              <template>

                <template v-if="cardEdit.editMode && cardEdit.writePermissions">
                  <div class="modal-undo-container-item">
                    <a href="#" v-on:click="deleteCardAlert=true" :style="{color: cardEdit.textColor}">Delete card</a>
                  </div>
                  <div class="modal-undo-container-item">
                    <button class="btn btn-primary-nextgen" v-b-tooltip.click
                            :title="getTitle()" triggers="click" v-on:click="togglePublishCard">
                      <template v-if="cardEdit.published">Unpublish</template>
                      <template v-if="!cardEdit.published">Publish</template>
                      card
                    </button>
                  </div>
                </template>

                <template v-if="!cardEdit.editMode || !cardEdit.writePermissions">
                  <template v-if="cardEdit.writePermissions">
                    <div class="modal-undo-container-item" :style="{color: cardEdit.textColor}">
                      Connect ↗
                    </div>
                  </template>

                  <div class="modal-undo-container-item">
                    <a href="#" v-on:click="loadShareAlert" :style="{color: cardEdit.textColor}">Share ↗</a>
                  </div>


                </template>

                <template v-if="cardEdit.writePermissions">
                  <div class="modal-undo-container-item d-flex justify-content-between">
                    <div style="padding-right: 10px;" :style="{color: cardEdit.textColor}">Edit</div>
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" id="flexSwitchCheckChecked"
                             v-model="cardEdit.editMode" v-on:click="toggleEditMode">
                    </div>
                  </div>
                </template>
              </template>
            </div>
          </div>
          <div class="suf-header" v-if="cardEdit.editMode" :style="{color: cardEdit.textColor}">
            <b :style="{color: cardEdit.textColor}">Title</b> &nbsp; Which title makes your way or mean for innovation
            easy to find?
          </div>


          <div class="modal-body" v-if="cardEdit.idCard">

            <FirstPage :card="cardEdit" v-show="currentPageNumber === 1" @updateCard="updateCardInfo"
                       :next-card="nextCardInfo('next')" :previous-card="nextCardInfo('previous')"></FirstPage>

            <div class="row justify-content-center">
              <div class="col-md-6 max-width-660">

                <!-- Tags Section -->
                <div class="card-tags-section" v-if="cardEdit.tags && cardEdit.tags.length > 0">
                  <div class="card-tags-wrapper">
                    <button class="card-tags-arrow card-tags-arrow-left" :class="{'invisible': !canScrollTagsLeft}"
                            @click="scrollTagsLeft" aria-label="Scroll left">
                      ←
                    </button>
                    <div class="card-tags-container" ref="tagsContainer" @scroll="updateTagsScrollState">
                      <span class="card-tag" v-for="(tag, index) in cardEdit.tags" :key="index">
                        #{{ tag }}
                      </span>
                    </div>
                    <button class="card-tags-arrow card-tags-arrow-right" :class="{'invisible': !canScrollTagsRight}"
                            @click="scrollTagsRight" aria-label="Scroll right">
                      →
                    </button>
                  </div>
                </div>

                <hr class="hr-card-divider"/>
                <div class="connected-card-controller d-flex justify-content-between">

                  <template v-if="!linkCardPanel">
                    <b v-if="cardEdit.published">Connected Cards ({{ cardEdit.cardConnections.length }})</b>

                    <b v-if="cardEdit.published"><a href="#" v-on:click.prevent="linkCardPanel = true"> + Connect
                      Card</a></b>
                    <b v-if="!cardEdit.published">In order to start linking cards, you need to publish your card
                      first</b>
                  </template>

                  <template v-if="linkCardPanel">
                    <b> Select one of your cards to connect to</b>

                    <b><a href="#" v-on:click.prevent="linkCardPanel = false"> Cancel</a></b>
                  </template>


                </div>
                <hr class="hr-card-divider"/>

                <div v-if="cardEdit.cardConnections.length > 0 && !linkCardPanel">
                  <a href="#" v-on:click="previousLinkedCardPage()"
                     :style="{ visibility: this.previousLinkedCardPage(true) ? 'visible' : 'hidden' }">←</a> Page
                  {{
                    this.linkedCardHelper.page
                  }}/{{ Math.round(cardEdit.cardConnections.length / this.linkedCardHelper.cardPerPage) }} <a
                    href="#" v-on:click="nextLinkedCardPage()" v-if="nextLinkedCardPage(true)">→</a>
                </div>
              </div>
            </div>

            <card-selecter-component v-show="linkCardPanel" :card="cardEdit"/>
            <div id="connectedCardGrid" v-touch:swipe="linkedCardsSwipeHandler" v-if="this.linkCardPanel !== true">
              <div class="card-deck" v-if="cardEdit.cardConnections.length > 0">

                <div class="card connectedCard" :style="{'background': connection.connectedCard.cardColor}"
                     :class="{'cardBorder': connection.connectedCard.border}"
                     v-for="connection in cardEdit.cardConnections.slice(linkedCardHelper.start, linkedCardHelper.end)"
                     :key="connection.connectedCard.idCard">

                  <a :href="'#/card/' + connection.connectedCard.idCard + '/'"
                     v-on:click="loadCard(connection.connectedCard)">
                    <div class="card-body">
                      <div class="margin-bottom">
                        <div class="card-text text-uppercase special2"
                             :style="{color: connection.connectedCard.textColor}">
                          {{ connection.connectedCard.title }}
                        </div>
                      </div>
                    </div>

                    <div class="card-footer">
                      <div class="col-md-12">
                        <div class="card-text special">
                          <div class="keep-bottom" :style="{color: connection.connectedCard.textColor}">
                            {{ connection.connectedCard.cardCompany.companyName }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                  <!-- Maximum of 2 cards width. A swipe gesture shall browse through the cards.                         -->

                  <div class="link-card-info">
                    <div class="link-card-metadata">
                      <div class="link-card-metadata-item">
                        <b>Connected by </b> <i><a href="#"
                                                   v-if="cardEdit.editMode && (cardEdit.cardCompany.companyName === connection.connectedBy)"
                                                   v-on:click="removeCardConnection(connection.card.idCard, connection.connectedCard.idCard)">Remove
                        connection</a></i><br>
                        {{ connection.connectedBy }}
                      </div>

                      <div class="link-card-metadata-item">
                        <b>Connected since</b> <br>
                        {{ connection.connectedSince }}
                      </div>
                    </div>

                    <div class="link-card-description">
                      <b>Comment</b> <i><a href="#"
                                           v-if="cardEdit.editMode && (cardEdit.cardCompany.companyName === connection.connectedBy)"
                                           v-on:click="openEditCommentPanel(connection)">Edit comment</a></i><br>
                      {{ connection.userNote }}
                    </div>
                  </div>
                </div>

              </div>
            </div>
            <ShareAlertComponent type="nobb-alert-primary" class="text-uppercase"
                                 message="Share this card now via mail or by copying this sharing-link"
                                 :link="'mailto:?body=' + emailBody" v-if="shareAlert"></ShareAlertComponent>
            <AlertComponent :message="this.errorAlert.message" type="nobb-alert-warning"
                            v-if="this.errorAlert.shown"></AlertComponent>

            <decision-alert-component type="nobb-alert-primary" ack-message="Yes delete this card"
                                      reject-message="No do not delete this card"
                                      message="Are you sure you want to delete this card? " v-if="deleteCardAlert"
                                      @acknowledged="deleteCard(cardEdit)"
                                      @rejected="deleteCardAlert=false"></decision-alert-component>
          </div>
        </div>
      </div>
    </div>
    <!-- End card Modal render !-->
  </div>
</template>

<script>
import FirstPage from './FirstPage'
import CardService from '../../services/CardService'
import ShareAlertComponent from '../alert/ShareAlertComponent'
import EventBus from '../../event-bus.js'
import _ from 'lodash'
import AlertComponent from '../alert/AlertComponent'
import DecisionAlertComponent from '../alert/DecisionAlertComponent'
import {Modal} from 'bootstrap'
import router from '../../router'
import CardSelecterComponent from "./CardSelecterComponent.vue";

export default {
  name: 'CardViewerComponent',
  components: {CardSelecterComponent, DecisionAlertComponent, AlertComponent, FirstPage, ShareAlertComponent},
  computed: {
    user() {
      return this.$store.state.user
    },
    orderedPages: function () {
      return _.orderBy(this.cardEdit.pages, 'pageNumber')
    },
    categoryHasOverflow() {
      return this.categoryCanScrollLeft || this.categoryCanScrollRight
    },
    authorHasOverflow() {
      return this.authorCanScrollLeft || this.authorCanScrollRight
    },
    tagHasOverflow() {
      return this.tagCanScrollLeft || this.tagCanScrollRight
    }
  },
  mounted() {

    // If direct linking, open card.
    if (this.$route.params.idCard) {
      console.log('Trying to open card with id: ' + this.$route.params.idCard)

      let openCard = []

      openCard['idCard'] = this.$route.params.idCard

      this.loadCard(openCard, true)
    }

    this.uid = Math.random().toString(36).substring(7)

    EventBus.$on('search-card', resp => {
      this.currentSearchQuery = resp
      this.loadAllCards()

      if (location.hash === '#' || location.hash !== '#/about') {
        location.hash = '#' + 'cardGrid'
      }
    })

    EventBus.$on('cards-refresh', () => {
      this.loadAllCards()
    })

    EventBus.$on('loadCard', (data) => {
      console.log("Loading Card + " + data)

      this.loadCard(data, false, true)
    })

    EventBus.$on('reloadCardConnections', (card) => {
      this.reloadCardConnections(card)
      this.linkCardPanel = false
    })

    this.cardEdit_copy = Object.assign(this.cardEdit)

    EventBus.$on('orderBy', resp => {
      this.orderBy = resp[0]
      this.orderByDirection = resp[1]

      this.loadAllCards()
    })

    this.loadAllCards()

    EventBus.$on('textContentChanged', (data) => {
      this.handleTextContentChanged(data.sectionId, data.content)
    })

    CardService.getAllCardCategories().then((response) => {
      this.cardCategories = Array.isArray(response.data) ? response.data : []
      this.$nextTick(this.updateCategoryScrollState)
    })

    CardService.getMostUsedTags().then((response) => {
      this.topTags = Array.isArray(response.data) ? response.data : []
      this.$nextTick(this.updateTagScrollState)
    })

    CardService.getAllCardAuthors().then((response) => {
      this.authors = Array.isArray(response.data) ? response.data : []
      this.$nextTick(this.updateAuthorScrollState)
    })

    window.addEventListener('resize', this.updateAllScrollStates)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateAllScrollStates)
  },
  methods: {
    async updateCategory(category) {
      this.cardEdit.cardCategory = category
      await this.updateCardInfo(this.cardEdit)
    },
    removeCardConnection(cardId, connectedCardId) {
      CardService.disonnectCard(cardId, connectedCardId).then(() => {
        let card = {
          idCard: cardId
        }
        this.reloadCardConnections(card)
      }).catch((error) => {
        console.log(error)
      })
    },
    changeFilter(filter) {
      this.filter = filter
      this.loadAllCards()
    },
    changeAuthorFilter(filter) {
      this.authorFilter = filter
      this.loadAllCards()
    },
    onRowWheel(refName, event) {
      const el = this.$refs[refName]
      if (!el) return
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
      el.scrollLeft += delta
    },
    scrollRow(refName, direction) {
      const el = this.$refs[refName]
      if (!el) return
      el.scrollBy({left: direction * Math.max(el.clientWidth * 0.8, 200), behavior: 'smooth'})
    },
    computeScrollFlags(refName) {
      const el = this.$refs[refName]
      if (!el) return {left: false, right: false}
      return {
        left: el.scrollLeft > 0,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      }
    },
    updateAuthorScrollState() {
      const f = this.computeScrollFlags('authorScroller')
      this.authorCanScrollLeft = f.left
      this.authorCanScrollRight = f.right
    },
    updateCategoryScrollState() {
      const f = this.computeScrollFlags('categoryScroller')
      this.categoryCanScrollLeft = f.left
      this.categoryCanScrollRight = f.right
    },
    updateTagScrollState() {
      const f = this.computeScrollFlags('tagScroller')
      this.tagCanScrollLeft = f.left
      this.tagCanScrollRight = f.right
    },
    updateAllScrollStates() {
      this.updateAuthorScrollState()
      this.updateCategoryScrollState()
      this.updateTagScrollState()
    },
    toggleCategoryExpand() {
      this.categoryExpanded = !this.categoryExpanded
      if (!this.categoryExpanded) {
        this.$nextTick(this.updateCategoryScrollState)
      }
    },
    toggleAuthorExpand() {
      this.authorExpanded = !this.authorExpanded
      if (!this.authorExpanded) {
        this.$nextTick(this.updateAuthorScrollState)
      }
    },
    toggleTagExpand() {
      this.tagExpanded = !this.tagExpanded
      if (!this.tagExpanded) {
        this.$nextTick(this.updateTagScrollState)
      }
    },
    changeTagFilter(filter) {
      this.tagFilter = filter
      this.loadAllCards()
    },
    getFilterState() {
      return this.filter;
    },
    loadShareAlert: function () {
      this.shareAlert = true
    },
    cardsUpdated() {
      console.log('Received an event')
    },
    loadErrorAlert: function (message) {
      this.errorAlert.shown = true
      this.errorAlert.message = message
    },
    setCardColors: function (cards) {
      for (let i = 0; i < cards.length; i++) {
        cards[i].textColor = (parseInt(cards[i].cardColor.replace('#', ''), 16) > 0xffffff / 2) ? '#000' : '#fff'
        if (cards[i].cardColor === '#ffffff') {
          cards[i].border = true
        }
      }
    },
    addSource: function (newSource) {
      let source = {
        text: newSource
      }

      this.cardEdit.sources.push(source)

      this.cardEdit.addSource = ''
    },
    loadPage: function (page) {
      this.cardEdit.currentPage = page
      this.currentPageNumber = page
    },
    getSectionIndex(item, position) {
      return item.findIndex(i => i.position === position)
    },
    azOrder: function () {
      EventBus.$emit('orderBy', ['title', this.orderByDirection])
      this.orderBy = 'title'

      // eslint-disable-next-line no-empty
      if (this.orderByDirection === "DESC") {
        this.orderByDirection = "ASC"
      } else {
        console.log("Flipping order by direction")
        this.orderByDirection = "DESC"
        console.log(this.orderByDirection)
      }
    },
    applyFilterOrderBy: function (by, direction) {
      console.log('Ordering by: ' + by + ' and direction: ' + direction);
      EventBus.$emit('orderBy', [by, direction])
      direction = ""
    },
    addCard() {
      if (this.user.loggedIn) {
        this.cardEdit = Object.assign(this.cardEdit_copy)
        this.cardEdit.cardColor = this.user.company.companyColor
        this.cardEdit.cardCompany = this.user.company
        this.cardEdit.cardType = 'DEFAULT'
        this.cardEdit.published = false
        this.cardEdit.new = true

        CardService.addNewCard(this.cardEdit)
            .then((response) => {
              this.cardEdit = response.data
              this.cardEdit.writePermissions = true
              this.cardEdit.pages = []
              this.cardEdit.published = false
              // this.cardEdit.textColor = (parseInt(this.cardEdit.cardColor.replace('#', ''), 16) > 0xffffff / 2) ? '#000' : '#fff'
              this.cardEdit.textColor = '#000'
              this.cardEdit.currentPage = 1 // Default to first page on add.
              this.currentPageNumber = 1 // Always default to the first page, when loading new cards.
              this.cardEdit.editMode = true

              this.cardPush = this.cardEdit

              // lastEditedBy
              this.cardPush.lastEditedBy = this.cardEdit['lastEditedBy']['idUser']

              this.cards.push(this.cardPush)
              this.$forceUpdate()

              let options = {
                focus: true
              }

              let cardEditModal = new Modal(document.getElementById(this.$refs['cardModal'].id), options)
              cardEditModal.show()
              EventBus.$emit('cardsUpdated', true)

              this.$router.push({path: '/card/' + this.cardEdit.idCard})
              location.reload()

            }).catch((error) => {
          console.error('Error' + error)
        })
      } else {
        this.loginAlert = true
      }
    },
    loadAllCards: function () {
      let categoryFilter = this.filter === 'all' ? null : this.filter
      let tagFilter = this.tagFilter === 'all' ? null : this.tagFilter
      // 'mine' is handled client-side so the backend isn't asked to filter by a non-existent author id
      let authorFilter = (this.authorFilter === 'all' || this.authorFilter === 'mine') ? null : this.authorFilter

      let promise
      if (this.currentSearchQuery) {
        promise = CardService.searchCards(this.currentSearchQuery, this.orderBy, this.orderByDirection, categoryFilter, tagFilter, authorFilter)
      } else {
        promise = CardService.getAllCards(this.orderBy, this.orderByDirection, false, categoryFilter, tagFilter, authorFilter)
      }

      promise.then((response) => {
        // The backend may return a non-array (e.g. false/{}) when a filter combination yields no results.
        this.cards = Array.isArray(response.data) ? response.data : []

        // Always show the user's own cards (published or not); hide other users' unpublished cards.
        this.cards = this.cards.filter(card => card.published === 1 || card.cardOwner === this.user.idUser)

        if (this.authorFilter === 'mine' && this.user.loggedIn) {
          this.cards = this.cards.filter(card => card.cardOwner === this.user.idUser)
        }

        this.setCardColors(this.cards)
      })
    },
    reloadCardConnections: function (card) {
      CardService.getCardInfo(card.idCard).then((response) => {
        if (response.data.cardConnections === false) {
          response.data.cardConnections = []
        }

        this.cardEdit.cardConnections = this.setCardConnectionsColors(response.data.cardConnections)
      })
    },
    loadCard: function (card, openModal = false, setRoute = false) {
      // Forcing the components to disappear before reinitializing again.
      this.shareAlert = false
      this.cardEdit = false
      this.currentPageNumber = 1 // Always default to the first page, when loading new cards.
      this.publishSure = false

      CardService.getCardInfo(card.idCard).then((response) => {
        this.cardEdit = response.data
        this.undo_card = response.data
        this.textContentMap = {}
        this.initialTags = response.data.tags || []

        this.cardEdit.writePermissions = false
        this.cardEdit.editMode = false
        // this.cardEdit.textColor = (parseInt(this.cardEdit.cardColor.replace('#', ''), 16) > 0xffffff / 2) ? '#000' : '#fff'
        this.cardEdit.textColor = '#000'
        this.cardEdit.published = +response.data.published

        if (this.user.company.idCompany === response.data.cardCompany.idCompany && !this.user.isSuspended) {
          this.cardEdit.writePermissions = true
        } else {
          // If the user owns the card, then he always should have writePermissions.
          if (this.cardEdit.lastEditedBy.idUser === this.user.idUser) {
            this.cardEdit.writePermissions = true
          }
        }

        console.log(this.cardEdit)

        // Auto enable editMode on unpublished cards
        if (this.cardEdit.writePermissions && this.cardEdit.published === 0) {
          this.toggleEditMode()
        }

        if (openModal) {
          let options = {
            backdrop: true,
            keyboard: true,
            focus: true
          }

          let cardEditModal = new Modal(document.getElementById(this.$refs['cardModal'].id), options)
          cardEditModal.show()
        }

        if (setRoute) {
          router.push({name: 'CardLinkingComponent', params: {idCard: card.idCard}}).catch(() => {
          })
        }

        this.cardEdit.cardConnections = this.setCardConnectionsColors(this.cardEdit.cardConnections)

        this.linkedCardHelper.start = 0
        this.linkedCardHelper.end = this.linkedCardHelper.cardPerPage

        this.initTagsScrollState()
      })
    },
    setCardConnectionsColors(cardConnections) {
      cardConnections.forEach((cardConnection) => {
        cardConnection.connectedCard.border = cardConnection.connectedCard.cardColor === '#ffffff';
        cardConnection.connectedCard['textColor'] = (parseInt(cardConnection.connectedCard.cardColor.replace('#', ''), 16) > 0xffffff / 2) ? '#000' : '#fff'
      })

      return cardConnections
    },
    nextCardInfo: function (direction = "next") {
      let direction_integer;
      if (direction === "next") {
        direction_integer = 1
      } else {
        direction_integer = -1
      }

      let currentCardIndex;
      currentCardIndex = this.cards.findIndex(card => card.idCard === this.cardEdit.idCard)
      const newCard = this.cards[currentCardIndex + direction_integer];

      return {
        available: typeof newCard !== "undefined",
        newCard: newCard,
      }

    },
    nextCard: function (direction = "next", cardAvailableCheck = false) {
      let direction_integer;
      if (direction === "next") {
        direction_integer = 1
      } else {
        direction_integer = -1
      }

      let currentCardIndex;
      currentCardIndex = this.cards.findIndex(card => card.idCard === this.cardEdit.idCard)

      const newCard = this.cards[currentCardIndex + direction_integer];

      if (typeof newCard !== "undefined" && !cardAvailableCheck) {
        let cardEditModal = Modal.getInstance(document.getElementById(this.$refs['cardModal'].id))
        cardEditModal.hide()

        this.loadCard(newCard, true, true)
      }

      if (cardAvailableCheck) {
        return typeof newCard !== "undefined"
      }

    },
    deleteCard: function (card) {
      CardService.deleteCard(card).then(() => {
        let cardEditModal = Modal.getInstance(document.getElementById(this.$refs['cardModal'].id))
        cardEditModal.hide()

        EventBus.$emit('cardsUpdated', true)
        EventBus.$emit('cards-refresh', true)
      })
    },
    updateCardInfo: function (card) {
      CardService.updateCardInfo(card).then((response) => {

        card = this.cards.find(x => x.idCard === response.data.idCard)
        card.title = response.data.title
        card.lastEdited = response.data.lastEdited
        card.lastEditedBy = response.data.lastEditedBy
        card.published = response.data.published
        card.cardColor = response.data.cardColor

        if (card.editMode) {
          EventBus.$emit('cardsUpdated', true)
          EventBus.$emit('cards-refresh', true)
        }
      })
    },
    togglePublishCard: function () {
      if (!this.publishSure) {
        this.publishSure = true
      } else {
        this.publishSure = false
        CardService.togglePublishCard(this.cardEdit).then(() => {
          this.cardEdit.published = !this.cardEdit.published
          let card = this.cards.find(x => x.idCard === this.cardEdit.idCard)
          card.published = this.cardEdit.published
          EventBus.$emit('cards-refresh', true)
        }).catch((error) => {
          console.error(error.response.data.message)
          this.loadErrorAlert(error.response.data.message)
        })
      }
    },
    toggleEditMode: function () {
      this.$set(this.cardEdit, 'editMode', !this.cardEdit.editMode)

      EventBus.$emit('modeChange', this.cardEdit.editMode)
      this.$forceUpdate()
    },
    getTitle() {
      if (this.cardEdit.published) {
        return 'Clicking this button again will hide your great work from the whole world, are you sure ?'
      }

      return 'Clicking this button again will really publish this card making it available to the whole world, are you sure? '
    },
    nextLinkedCardPage(validationOnly = false) {
      if (validationOnly) {
        return this.linkedCardHelper.end < this.cardEdit.cardConnections.length
      }

      this.linkedCardHelper.start += this.linkedCardHelper.cardPerPage
      this.linkedCardHelper.end += this.linkedCardHelper.cardPerPage
      this.linkedCardHelper.page += 1
    },
    previousLinkedCardPage(validationOnly = false) {
      if (validationOnly) {
        return this.linkedCardHelper.start > 0
      }

      this.linkedCardHelper.start -= this.linkedCardHelper.cardPerPage
      this.linkedCardHelper.end -= this.linkedCardHelper.cardPerPage
      this.linkedCardHelper.page -= 1
    },
    linkedCardsSwipeHandler(direction) {
      console.log('Handler!' + direction)

      if (direction === 'right' && this.previousLinkedCardPage(true)) {
        this.previousLinkedCardPage()
      } else if (direction === 'left' && this.nextLinkedCardPage(true)) {
        this.nextLinkedCardPage()
      }


    },
    openEditCommentPanel(connection) {
      this.linkCardPanel = true
      EventBus.$emit("select-connection", connection)
    },
    extractTagsFromText(text) {
      if (!text) return []
      const regex = /#(\w+)/g
      const tags = []
      let match
      while ((match = regex.exec(text)) !== null) {
        tags.push(match[1])
      }
      return tags
    },
    updateTagsFromContent() {
      let allExtractedTags = []
      Object.values(this.textContentMap).forEach(content => {
        allExtractedTags = allExtractedTags.concat(this.extractTagsFromText(content))
      })

      // Unique tags from content + initial backend tags
      const uniqueTags = _.uniq([...this.initialTags, ...allExtractedTags])
      this.$set(this.cardEdit, 'tags', uniqueTags)

      // Ensure arrows are updated if new tags appear
      this.initTagsScrollState()
    },
    handleTextContentChanged(sectionId, content) {
      this.$set(this.textContentMap, sectionId, content)
      this.updateTagsFromContent()
    },
    scrollTagsLeft() {
      const container = this.$refs.tagsContainer
      if (container) {
        container.scrollBy({left: -150, behavior: 'smooth'})
      }
    },
    scrollTagsRight() {
      const container = this.$refs.tagsContainer
      if (container) {
        container.scrollBy({left: 150, behavior: 'smooth'})
      }
    },
    updateTagsScrollState() {
      const container = this.$refs.tagsContainer
      if (container) {
        this.canScrollTagsLeft = container.scrollLeft > 0
        this.canScrollTagsRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 1)
      }
    },
    initTagsScrollState() {
      // Use setTimeout to wait for DOM to be fully rendered
      setTimeout(() => {
        const container = this.$refs.tagsContainer
        if (container) {
          const hasOverflow = container.scrollWidth > container.clientWidth
          this.canScrollTagsRight = hasOverflow
          this.canScrollTagsLeft = container.scrollLeft > 0

          // Set up ResizeObserver for responsive updates
          if (!this.tagsResizeObserver) {
            this.tagsResizeObserver = new ResizeObserver(() => {
              this.updateTagsScrollState()
              // Also check if there's overflow
              const c = this.$refs.tagsContainer
              if (c && c.scrollLeft === 0) {
                this.canScrollTagsRight = c.scrollWidth > c.clientWidth
              }
            })
            this.tagsResizeObserver.observe(container)
          }
        }
      }, 200)
    }
  },
  data() {
    return {
      uid: '',
      hideSourceAdd: false,
      linkCardPanel: false,
      canScrollTagsLeft: false,
      canScrollTagsRight: true,
      categoryCanScrollLeft: false,
      categoryCanScrollRight: false,
      tagCanScrollLeft: false,
      tagCanScrollRight: false,
      textContentMap: {},
      initialTags: [],
      cards: [],
      cardEdit_copy: false,
      undo_card: {},
      shareAlert: false,
      errorAlert: {message: '', shown: false},
      loginAlert: false,
      deleteCardAlert: false,
      filter: 'all',
      orderByDirection: 'DESC',
      orderBy: 'lastEdited',
      cardCategories: [],
      authors: ['Pen Name', 'Pen Name', 'Pen Name', 'Pen Name', 'Pen Name', 'Pen Name', 'Pen Name'],
      topTags: ['#Banana', '#Banana', '#Banana', '#Banana', '#Banana', '#Banana', '#Banana', '#Banana', '#Banana', '#Banana', '#Banana', '#Banana'],
      authorFilter: 'all',
      authorCanScrollLeft: false,
      authorCanScrollRight: false,
      categoryExpanded: false,
      authorExpanded: false,
      tagExpanded: false,
      tagFilter: 'all',
      sortDirection: "DESC",
      linkedCardHelper: {
        start: 0,
        end: 0,
        page: 1,
        cardPerPage: 2
      },
      cardEdit: {
        idCard: '',
        title: 'THE MEANS\' CATCHY TITLE!',
        lastEdited: '',
        lastEditedBy: '',
        cardColor: '',
        cardCompany: '',
        cardType: 'DEFAULT',
        published: false,
        writePermissions: true,
        editMode: false,
        currentPage: 1,
        cardConnections: [
          {
            connectedCard: {}
          }
        ],
        textColor: '#000',
        orderBy: 'lastEdited',
        pages: [{
          'idCard': 5,
          'idCardPage': 1,
          'pageNumber': 1,
          'title': ''
        },
          {
            'idCardPage': 2,
            'idCard': 6,
            'pageNumber': 2,
            'title': ''
          }]
      },
      VUE_APP_NOBB_API: process.env.VUE_APP_NOBB_API,
      currentPageNumber: 1,
      searchQuery: '',
      publishSure: false,
      emailBody: 'Hi, %0D%0A' +
          'maybe that\'s the inspiration you needed?! %0D%0A' +
          'Check this awesome card on innovation on nobb.io: ' +
          window.location.href + ' %0D%0A' +
          'nobb.io – what is it?! %0D%0A' +
          '"How does innovation come to life?" %0D%0A' +
          'Driven by the curiosity to find answers to this question, we created nobb.io. %0D%0A' +
          'It is a platform for sharing experience, ideas, methods, strategies and ways and means for innovation among like-minded innovators. %0D%0A' +
          'Learn more on nobb.io %0D%0A' +
          'HAPPY SHARING %0D%0A' +
          'best, %0D%0A' +
          'the nobbs',
    }
  },
  watch: {
    user: function () {
      this.loadAllCards()
      console.info('Refreshing card render since user state changed. ')
    },
    'cardEdit.tags': {
      handler() {
        setTimeout(() => {
          this.initTagsScrollState()
        }, 100)
      },
      immediate: false
    }
  }
}
</script>
<style>

textarea:disabled {
  color: black;
}

button:disabled {
  background-color: #707070 !important;
  border-color: #707070 !important;
  cursor: not-allowed !important;
}

.mobile-hr-divider {
  margin-left: 0;
  width: 10%;
  color: black;
  height: 1px;
  background-color: black;
}

/* Custom, non global component styling. */
.textarea-spacer {
  min-height: 2.0vw;
}

.add-source-form {
  background: none;
  border: none;
  font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
  font-size: 10pt;
}

.publish-btn {
  position: absolute;
  width: 178px;
  height: 46px;
  top: 100%;
  left: 40%;
  font-size: 25px;
}

.share-btn {
  position: absolute;
  width: 50px;
  height: 50px;
  top: 100%;
  left: -100px;
  font-size: 14px;
  border-radius: 50px;
  transform: none !important;
  font-style: italic;
}

.delete-card {
  z-index: -1000;
  /*width: 178px;*/
  /*height: 46px;*/
  position: absolute;
  top: 110%;
  left: 45%;
}

.logo-centered {
  float: left;
  max-height: 60px;
  margin: 10px;
}

.logo-container {
  position: absolute;
  /*background: white;*/
  /*border-radius: 2px;*/
  border: none;
  margin-top: -16px !important;
}

.small-subtext {
  font-size: 10pt;
  color: white !important;
  z-index: 0 !important;
}

span:hover {
  cursor: pointer;
}

.cardAuthor {
  position: absolute;
}

.modal-dismiss {
  font-size: 2.5em;
}

/* Card Tags Section */
.card-tags-section {
  margin-bottom: 20px;
  padding-top: 10px;
}

.card-tags-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-tags-arrow {
  background: none;
  border: none;
  font-size: 18px;
  color: #666;
  cursor: pointer;
  padding: 4px 8px;
  flex-shrink: 0;
  transition: color 0.2s;
}

.card-tags-arrow:hover {
  color: #000;
}

.card-tags-arrow.invisible {
  visibility: hidden;
  pointer-events: none;
}

.card-tags-container {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding-bottom: 5px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.card-tags-container::-webkit-scrollbar {
  display: none;
}

.card-tag {
  display: inline-block;
  background-color: #fff;
  color: #000;
  border: 1px solid #000;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

/* New Filter Styles */
.filter-main-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  margin-top: 10px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.filter-row:last-child {
  margin-bottom: 0;
}

.scroll-row-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 15px;
}

.scroll-row-wrapper:last-child {
  margin-bottom: 0;
}

.scroll-row-all {
  flex: 0 0 auto;
}

.filter-row--scroll {
  flex: 1 1 auto;
  flex-wrap: nowrap;
  margin-bottom: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 2px 4px;
  min-width: 0;
}

.filter-row--scroll::-webkit-scrollbar {
  display: none;
}

.filter-row--scroll .filter-pill {
  flex: 0 0 auto;
}

.scroll-row-arrow {
  flex: 0 0 auto;
  width: 20px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #000;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.scroll-row-arrow.is-disabled {
  opacity: 0.2;
  cursor: default;
  pointer-events: none;
}

.scroll-row-arrow.is-hidden {
  visibility: hidden;
  pointer-events: none;
}

.scroll-row-toggle {
  font-size: 18px;
  font-weight: 600;
}

.scroll-row-wrapper--expanded {
  align-items: flex-start;
}

.scroll-row-wrapper--expanded .scroll-row-all {
  margin-top: 2px;
}

.scroll-row-wrapper--expanded .scroll-row-toggle {
  margin-top: 2px;
}

.filter-row--wrap {
  flex: 1 1 auto;
  flex-wrap: wrap;
  margin-bottom: 0;
  overflow: visible;
  padding: 2px 4px;
  min-width: 0;
}

.filter-row--wrap .filter-pill {
  flex: 0 0 auto;
}

.filter-pill {
  border: 1px solid #000;
  border-radius: 8px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 14px;
  background: #fff;
  color: #000;
  transition: all 0.2s ease;
  font-family: Arial, sans-serif;
}

.filter-pill.active {
  background: #000 !important;
  color: #fff !important;
}

.filter-header-label, .sort-filter-label a {
  font-weight: normal;
  font-size: 18px;
  color: #000;
  text-decoration: underline;
  cursor: pointer;
}

.sort-filter-label .dropdown-toggle::after {
  display: none;
}

/* Map control (options) styles — visually related to filter pills but
   set apart via a fully-rounded shape, a labeled row, and a dashed
   accent on the navigation link, so they read as controls not filters.
   Mirrors the layout used on the card map view for consistency. */
.control-main-container {
  background: #fafafa;
  border: 1px dashed #bbb;
  border-radius: 8px;
  margin-top: 10px;
}

.control-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.control-row-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #555;
  margin-right: 4px;
}

.control-pill {
  border: 1px solid #000;
  border-radius: 999px;
  padding: 6px 16px;
  cursor: pointer;
  font-size: 14px;
  background: #fff;
  color: #000;
  transition: all 0.2s ease;
  font-family: Arial, sans-serif;
  text-decoration: none;
  display: inline-block;
  line-height: 1.4;
}

.control-pill:hover {
  background: #f0f0f0;
}

.control-pill.active {
  background: #000 !important;
  color: #fff !important;
}

.control-pill--link {
  margin-left: auto;
  border-style: dashed;
}

.control-pill--link:hover {
  background: #f0f0f0;
  color: #000;
}
</style>


